import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Form, Spin } from "antd";
import { Check, Link2 } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import apiClient from "../../../../helpers/apiClient";
import { useOnboarding } from "../../../../contexts/OnboardingContext";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import { useCategoriesContext } from "../../../../contexts/CategoriesContext";
import { useToast } from "../../../../contexts/ToastContext";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";
import stripeImage from "../../../../assets/icons/stripe.svg";
import ErrorModal from "../../../settings/components/integration/insurance/ErrorModal";
import TeqMobilityConnectionModal from "../../../settings/components/integration/insurance/TeqMobilityConnectionModal";
import { fetchTeqMobilityCoverageStates } from "../../../insurance/lib/insuranceSaleApi";
import {
    buildOnboardingInsuranceProviders,
    getOnboardingInsuranceProviderKeys,
    hasRequiredOnboardingInsurance,
    isProviderEnabled,
    resolveCompanyLocationState,
} from "../../utils/onboardingInsuranceProviders";

const ConnectActionButton = ({ connected, loading, onClick, connectLabel, connectedLabel }) => {
    if (connected) {
        return (
            <button
                type="button"
                disabled
                className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg bg-[#5d5dff] px-4 text-sm font-medium text-white">
                <span className="flex h-4 w-4 items-center justify-center rounded-full border border-white">
                    <Check size={10} strokeWidth={3} />
                </span>
                {connectedLabel}
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={loading}
            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border border-[#5d5dff] bg-white px-4 text-sm font-medium text-[#5d5dff] hover:bg-[#5d5dff]/5 disabled:opacity-60">
            {loading ? <Spin size="small" /> : <Link2 size={16} strokeWidth={2} aria-hidden />}
            {connectLabel}
        </button>
    );
};

ConnectActionButton.propTypes = {
    connected: PropTypes.bool,
    loading: PropTypes.bool,
    onClick: PropTypes.func,
    connectLabel: PropTypes.string.isRequired,
    connectedLabel: PropTypes.string.isRequired,
};

ConnectActionButton.defaultProps = {
    connected: false,
    loading: false,
    onClick: undefined,
};

const PaymentInsurance = forwardRef((props, ref) => {
    const { t } = useTranslation("onboarding");
    const { t: tSettings } = useTranslation("settings");
    const history = useHistory();
    const location = useLocation();
    const toast = useToast();
    const { updateStepProgress, stepStatus, STEP_KEY_TO_INDEX } = useOnboarding();
    const { myCompanyData, fetchCompanyData } = useUser();
    const { locations, fetchLocations } = useCategoriesContext();

    const [connectingStripe, setConnectingStripe] = useState(false);
    const [connectingProviderId, setConnectingProviderId] = useState(null);
    const [teqMobilityModalOpen, setTeqMobilityModalOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModalTitle, setErrorModalTitle] = useState("");
    const [errorModalMessage, setErrorModalMessage] = useState("");
    const [errorModalUrl, setErrorModalUrl] = useState("");
    const [coverageStates, setCoverageStates] = useState([]);
    const [coverageLoading, setCoverageLoading] = useState(true);
    const processedStripeReturn = useRef(false);
    const insuranceSectionRef = useRef(null);
    const [insuranceRequiredVisible, setInsuranceRequiredVisible] = useState(false);

    useEffect(() => {
        fetchLocations?.();
    }, [fetchLocations]);

    useEffect(() => {
        let cancelled = false;
        const loadCoverageStates = async () => {
            setCoverageLoading(true);
            try {
                const states = await fetchTeqMobilityCoverageStates();
                if (!cancelled) {
                    setCoverageStates(states);
                }
            } finally {
                if (!cancelled) {
                    setCoverageLoading(false);
                }
            }
        };
        loadCoverageStates();
        return () => {
            cancelled = true;
        };
    }, []);

    const companyLocationState = useMemo(
        () => resolveCompanyLocationState(myCompanyData, locations),
        [myCompanyData, locations],
    );

    const onboardingProviderKeys = useMemo(
        () => getOnboardingInsuranceProviderKeys(companyLocationState, coverageStates),
        [companyLocationState, coverageStates],
    );

    const onboardingProviders = useMemo(
        () => buildOnboardingInsuranceProviders(companyLocationState, coverageStates),
        [companyLocationState, coverageStates],
    );

    const stripeConnected = useMemo(() => {
        const stripeGateway = myCompanyData?.paymentGateways?.find((gateway) => gateway.type === "stripe");
        return Boolean(stripeGateway?.isConnected);
    }, [myCompanyData?.paymentGateways]);

    const insuranceRows = useMemo(() => {
        const insuranceArray = myCompanyData?.insurance || [];
        return onboardingProviders.map((provider) => {
            const companyProvider = insuranceArray.find((item) => item.provider === provider.provider);
            return {
                ...provider,
                enabled: isProviderEnabled(companyProvider),
            };
        });
    }, [myCompanyData?.insurance, onboardingProviders]);

    const hasConnectedInsurance = useMemo(
        () => hasRequiredOnboardingInsurance(myCompanyData, onboardingProviderKeys),
        [myCompanyData, onboardingProviderKeys],
    );

    useEffect(() => {
        if (hasConnectedInsurance) {
            setInsuranceRequiredVisible(false);
        }
    }, [hasConnectedInsurance]);

    useImperativeHandle(
        ref,
        () => ({
            validateForContinue: () => {
                const ok = hasRequiredOnboardingInsurance(myCompanyData, onboardingProviderKeys);
                if (!ok) {
                    setInsuranceRequiredVisible(true);
                    requestAnimationFrame(() => {
                        insuranceSectionRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    });
                }
                return ok;
            },
        }),
        [myCompanyData, onboardingProviderKeys],
    );

    useEffect(() => {
        if (!stepStatus.stripe && stripeConnected) {
            updateStepProgress("stripe", STEP_KEY_TO_INDEX.stripe ?? 4, true);
        }
    }, [stripeConnected, stepStatus.stripe, updateStepProgress, STEP_KEY_TO_INDEX.stripe]);

    useEffect(() => {
        if (!stepStatus.insurance && hasConnectedInsurance) {
            updateStepProgress("insurance", STEP_KEY_TO_INDEX.insurance ?? 5, true);
        }
    }, [hasConnectedInsurance, stepStatus.insurance, updateStepProgress, STEP_KEY_TO_INDEX.insurance]);

    const clearStripeReturnParams = useCallback(() => {
        history.replace("/onboarding?step=stripe");
    }, [history]);

    const updateStripeOnboardingResult = useCallback(
        async (query) => {
            let isSuccess = query.get("stripe-onboard-connection-success");
            if (isSuccess === "true" && query.get("stripe-account-id")) {
                isSuccess = true;
            } else if (isSuccess === "false") {
                isSuccess = false;
            } else {
                isSuccess = null;
            }

            try {
                const res = await apiClient.post("/api/payment/update-company-onboard-success-status", {
                    isSuccess,
                    accountId: query.get("stripe-account-id"),
                });

                if (res?.data?.success) {
                    await fetchCompanyData();
                    toast.success({
                        message: t("steps.paymentInsurance.connectionSuccess"),
                    });
                }
            } catch (error) {
                if (error?.response?.data?.message === "Stripe account is not completed") {
                    toast.error({
                        message: t("steps.paymentInsurance.incompleteAccount"),
                    });
                } else {
                    toast.error({
                        message: tSettings("integrations.stripeOnboardingError"),
                    });
                }
            } finally {
                clearStripeReturnParams();
            }
        },
        [clearStripeReturnParams, fetchCompanyData, toast, t, tSettings],
    );

    useEffect(() => {
        if (processedStripeReturn.current || !myCompanyData) return;

        const query = new URLSearchParams(location.search);
        const stripeFailed = query.get("stripe-onboard-connection-failed");
        const stripeSuccess = query.get("stripe-onboard-connection-success");

        if (stripeFailed) {
            processedStripeReturn.current = true;
            toast.error({
                message: t("steps.paymentInsurance.incompleteAccount"),
            });
            clearStripeReturnParams();
            return;
        }

        if (stripeSuccess) {
            processedStripeReturn.current = true;
            updateStripeOnboardingResult(query);
        }
    }, [clearStripeReturnParams, location.search, myCompanyData, toast, t, updateStripeOnboardingResult]);

    const connectStripe = async () => {
        try {
            setConnectingStripe(true);
            const res = await apiClient.post("/api/payment/onboard-stripe-account", {
                originUrl: window.location.origin,
                returnPath: "/onboarding?step=stripe",
            });

            if (res?.data?.success && res?.data?.message === "Connected with existing stripe account") {
                await fetchCompanyData();
                toast.success({
                    message: t("steps.paymentInsurance.connectionSuccess"),
                    description: t("steps.paymentInsurance.existingAccountConnected"),
                });
                return;
            }

            if (res?.data?.success) {
                const url = res.data.data?.url;
                if (url) {
                    window.location.href = url;
                    return;
                }
            }

            throw new Error(res?.data?.message || t("steps.paymentInsurance.connectionFailed"));
        } catch {
            toast.error({
                message: t("steps.paymentInsurance.connectionFailed"),
                description: tSettings("integrations.somethingWentWrong"),
            });
        } finally {
            setConnectingStripe(false);
        }
    };

    const handleTeqMobilityConnection = async (formData) => {
        if (!selectedProvider) return;

        try {
            setConnectingProviderId(selectedProvider.id);

            const agreementResponse = await apiClient.post("/api/insurance/generate-teqMobility-agreement", formData);
            if (!agreementResponse || !agreementResponse.data.success) {
                throw new Error("Failed to generate TeqMobility agreement");
            }

            const response = await apiClient.post("/api/insurance/connect-with-teqmobility");
            if (response && response.data.success) {
                toast.success({
                    message: tSettings("integrations.insuranceConnectionSuccess"),
                });
                await fetchCompanyData();
            }
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || error.message || tSettings("integrations.genericErrorDescription");
            const cleanedMessage = errorMessage.includes(":") ? errorMessage.split(":")[1] : errorMessage;

            setErrorModalMessage(cleanedMessage);
            setErrorModalOpen(true);
            setErrorModalTitle(tSettings("integrations.insuranceConnectionErrorTitle"));
            setErrorModalUrl("/settings?menu=companyInfo&edit=true");
        } finally {
            setConnectingProviderId(null);
            setTeqMobilityModalOpen(false);
            setSelectedProvider(null);
        }
    };

    const handleConnectInsurance = async (provider) => {
        if (connectingProviderId) return;

        if (provider.provider === "teqmobility") {
            setSelectedProvider(provider);
            setTeqMobilityModalOpen(true);
            return;
        }

        try {
            setConnectingProviderId(provider.id);

            let response;
            if (provider.provider === "bonzah") {
                response = await apiClient.post("/api/insurance/connect-with-bonzah");
            }

            if (response && response.data.success) {
                toast.success({
                    message: tSettings("integrations.insuranceConnectionSuccess"),
                });
                await fetchCompanyData();
            }
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || error.message || tSettings("integrations.genericErrorDescription");
            const cleanedMessage = errorMessage.includes(":") ? errorMessage.split(":")[1] : errorMessage;
            toast.error({
                message: tSettings("integrations.insuranceConnectionErrorTitle"),
                description: cleanedMessage,
            });
        } finally {
            setConnectingProviderId(null);
        }
    };

    return (
        <div>
            <h1 className="text-[28px] font-bold tracking-tight text-gray-900 sm:text-[32px]">
                {t("steps.paymentInsurance.heading")}
            </h1>
            <p className="mt-1 mb-8 text-sm leading-relaxed text-gray-500 sm:text-base">
                {t("steps.paymentInsurance.description")}
            </p>

            <section className="mb-10">
                <h2 className="text-base font-semibold text-gray-900 sm:text-lg">
                    {t("steps.paymentInsurance.paymentSectionTitle")}
                </h2>
                <p className="mt-1 mb-5 text-sm leading-relaxed text-gray-500">
                    {t("steps.paymentInsurance.paymentSectionDescription")}
                </p>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                    <div className="flex h-20 w-44 shrink-0 items-center justify-start sm:h-[88px] sm:w-[200px]">
                        <img
                            src={stripeImage}
                            alt="Stripe"
                            className="max-h-full max-w-full object-contain object-left"
                        />
                    </div>
                    <p className="min-w-0 flex-1 text-sm leading-relaxed text-gray-600">
                        {tSettings("integrations.stripeDescription")}
                    </p>
                    <ConnectActionButton
                        connected={stripeConnected}
                        loading={connectingStripe}
                        onClick={connectStripe}
                        connectLabel={t("steps.paymentInsurance.connect")}
                        connectedLabel={t("steps.paymentInsurance.connected")}
                    />
                </div>
            </section>

            <section ref={insuranceSectionRef} className="scroll-mt-6">
                <Form requiredMark={customizeRequiredMark} colon={false} layout="vertical" component={false}>
                    <Form.Item
                        label={t("steps.paymentInsurance.insuranceSectionTitle")}
                        required
                        validateStatus={insuranceRequiredVisible ? "error" : undefined}
                        help={insuranceRequiredVisible ? t("steps.paymentInsurance.insuranceRequired") : undefined}
                        className="[&_.ant-form-item-label>label]:!text-base [&_.ant-form-item-label>label]:!font-semibold [&_.ant-form-item-label>label]:!text-gray-900 sm:[&_.ant-form-item-label>label]:!text-lg">
                        <p className="mb-5 max-w-2xl text-sm leading-relaxed text-gray-500">
                            {t("steps.paymentInsurance.insuranceSectionDescription")}
                        </p>

                        {coverageLoading ? (
                            <div className="flex justify-center py-8">
                                <Spin />
                            </div>
                        ) : (
                            <ul className="flex flex-col gap-8">
                                {insuranceRows.map((provider) => (
                                    <li
                                        key={provider.provider}
                                        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                                        <div className="flex h-20 w-44 shrink-0 items-center justify-start sm:h-[88px] sm:w-[200px]">
                                            <img
                                                src={provider.img}
                                                alt={t(provider.nameKey)}
                                                className="max-h-full max-w-full object-contain object-left"
                                            />
                                        </div>
                                        <p className="min-w-0 flex-1 text-sm leading-relaxed text-gray-600">
                                            {tSettings(provider.descriptionKey)}
                                        </p>
                                        <ConnectActionButton
                                            connected={provider.enabled}
                                            loading={connectingProviderId === provider.id}
                                            onClick={() => handleConnectInsurance(provider)}
                                            connectLabel={t("steps.paymentInsurance.connect")}
                                            connectedLabel={t("steps.paymentInsurance.connected")}
                                        />
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Form.Item>
                </Form>
            </section>

            <TeqMobilityConnectionModal
                open={teqMobilityModalOpen}
                onCancel={() => {
                    setTeqMobilityModalOpen(false);
                    setSelectedProvider(null);
                }}
                onComplete={handleTeqMobilityConnection}
                loading={connectingProviderId === selectedProvider?.id}
            />

            <ErrorModal
                open={errorModalOpen}
                handleClose={() => setErrorModalOpen(false)}
                title={errorModalTitle}
                message={errorModalMessage}
                url={errorModalUrl}
            />
        </div>
    );
});

PaymentInsurance.displayName = "PaymentInsurance";

export default PaymentInsurance;
